import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './About.css';

// SEO: page-specific meta added via Helmet below

const About = () => {
  const [visibleElements, setVisibleElements] = useState([]);
  const [counters, setCounters] = useState({
    hospitals: 0,
    patients: 0,
    uptime: 0,
    support: 0
  });

  const sectionRef = useRef(null);
  const hasAnimated = useRef(false);

  const stats = useMemo(() => [
    { key: 'hospitals', value: 5, suffix: '+', label: 'Hospitals Trust Us' },
    { key: 'patients', value: 10, suffix: 'K+', label: 'Patients Served' },
    { key: 'uptime', value: 99.9, suffix: '%', label: 'System Uptime' },
    { key: 'support', value: 24, suffix: '/7', label: 'Support Available' }
  ], []);

  const values = [
    {
      icon: '🏥',
      title: 'Healthcare First',
      description: 'Every decision we make in our healthcare management system prioritizes patient care and healthcare outcomes.'
    },
    {
      icon: '🔒',
      title: 'Security & Privacy',
      description: 'Our EMR software is HIPAA compliant with enterprise-grade security protecting sensitive data.'
    },
    {
      icon: '🚀',
      title: 'Innovation',
      description: 'Our hospital software is continuously evolving with cutting-edge technology and user feedback.'
    },
    {
      icon: '🤝',
      title: 'Partnership',
      description: 'We work alongside healthcare providers as trusted technology partners, providing them with the best clinic management software.'
    }
  ];

  const animateCounters = useCallback(() => {
    stats.forEach(stat => {
      let current = 0;
      const increment = stat.value / 50;
      const timer = setInterval(() => {
        current += increment;
        if (current >= stat.value) {
          current = stat.value;
          clearInterval(timer);
        }
        setCounters(prev => ({
          ...prev,
          [stat.key]: current
        }));
      }, 30);
    });
  }, [stats]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.dataset.element;
            setVisibleElements(prev => [...prev, elementId]);

            if (elementId === 'stats' && !hasAnimated.current) {
              animateCounters();
              hasAnimated.current = true;
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('[data-element]');
    elements?.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [animateCounters]);

  const formatValue = (key, value) => {
    if (key === 'uptime') return value.toFixed(1);
    if (key === 'patients') return value.toFixed(1);
    return Math.floor(value);
  };

  return (
    <section id="about" className="about" ref={sectionRef}>
      <div className="container">
        <div className="about__content">
          {/* Hero Text */}
          <div 
            className={`about__hero ${visibleElements.includes('hero') ? 'about__hero--visible' : ''}`}
            data-element="hero"
          >
            <Helmet>
              <title>About Mediway Nextgen — Hospital Management System (HMS) India</title>
              <meta name="description" content="Learn about Mediway Nextgen, a hospital management system (HMS) used by hospitals in India for OPD, IPD, medical billing, and EMR workflows." />
              <link rel="canonical" href="https://mediwaynextgen.wayug.com/#about" />
              <meta property="og:title" content="About Mediway Nextgen — Hospital Management System India" />
              <meta property="og:description" content="Mediway Nextgen provides hospital ERP, OPD & IPD management, billing and EMR software for Indian hospitals." />
              <meta property="og:url" content="https://mediwaynextgen.wayug.com/#about" />
              <meta property="og:type" content="website" />
            </Helmet>

            <div className="about__text">
              <h2 className="about__title">
                Trusted by Healthcare Leaders across India for our Hospital Management System
              </h2>
              <p className="about__description">
                For over a decade, we've been at the forefront of healthcare technology, providing top-tier clinical and administrative systems. As the <strong>best HMS for private hospital India</strong> has, we understand the distinct challenges faced by growing facilities. Our platform is designed specifically to serve as the leading <strong>HMS software for 20+ bed hospital</strong> setups, offering premium quality and standard compliance at an affordable <strong>hospital software price India</strong> administrators appreciate.
              </p>
              <div className="about__highlights">
                <div className="highlight">
                  <div className="highlight__icon">✓</div>
                  <span>ISO/IEC 20000-1:2018</span>
                </div>
                <div className="highlight">
                  <div className="highlight__icon">✓</div>
                  <span>HIPAA Compliant</span>
                </div>
                <div className="highlight">
                  <div className="highlight__icon">✓</div>
                  <span>ISO 9001:2015</span>
                </div>
              </div>
            </div>

            <div className="about__image">
              <div className="image-container">
                <div className="team-photo">
                  <div className="photo-placeholder">
                    <div className="team-icons">
                      <div className="team-member">👨‍⚕️</div>
                      <div className="team-member">👩‍⚕️</div>
                      <div className="team-member">👨‍💻</div>
                      <div className="team-member">👩‍💻</div>
                    </div>
                  </div>
                </div>

                <div className="floating-cards">
                  <div className="floating-card floating-card--1">
                    <div className="card-icon">🏆</div>
                    <div className="card-text">Award Winning</div>
                  </div>

                  <div className="floating-card floating-card--2">
                    <div className="card-icon">⚡</div>
                    <div className="card-text">Lightning Fast</div>
                  </div>

                  <div className="floating-card floating-card--3">
                    <div className="card-icon">🔧</div>
                    <div className="card-text">24/7 Support</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div 
            className={`about__stats ${visibleElements.includes('stats') ? 'about__stats--visible' : ''}`}
            data-element="stats"
          >
            <div className="stats-grid">
              {stats.map((stat, index) => (
                <div key={stat.key} className="stat-card">
                  <div className="stat-number">
                    {formatValue(stat.key, counters[stat.key])}{stat.suffix}
                  </div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Values */}
          <div 
            className={`about__values ${visibleElements.includes('values') ? 'about__values--visible' : ''}`}
            data-element="values"
          >
            <h3 className="values__title">Our Core Values</h3>
            <div className="values-grid">
              {values.map((value, index) => (
                <div key={index} className="value-card">
                  <div className="value-card__icon">
                    <span>{value.icon}</span>
                  </div>
                  <h4 className="value-card__title">{value.title}</h4>
                  <p className="value-card__description">{value.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial Quote */}
          <div 
            className={`about__testimonial ${visibleElements.includes('testimonial') ? 'about__testimonial--visible' : ''}`}
            data-element="testimonial"
          >
            <div className="testimonial-card">
              <div className="testimonial__quote">
                "Mediway Nextgen's hospital management software has completely transformed our hospital operations. The patient management system is now effortless, and staff productivity has improved by 45%. This HMS software is simple to use and patients find the mobile app extremely helpful."
              </div>
              <div className="testimonial__author">
                <div className="author__avatar">
                  <span>👨‍⚕️</span>
                </div>
                <div className="author__info">
                  <div className="author__name">Dr. Anita Verma</div>
                  <div className="author__title">Chief Medical Officer</div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div 
            className={`about__cta ${visibleElements.includes('cta') ? 'about__cta--visible' : ''}`}
            data-element="cta"
          >
            <div className="cta-content">
              <h3>Ready to Transform Your Hospital?</h3>
              <p>Join hundreds of healthcare facilities already using Mediway Nextgen HMS</p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Start Your Free Trial
                </Link>
                <Link to="/features" className="btn btn-secondary">
                  Schedule a Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="about__background">
        <div className="background-grid"></div>
        <div className="background-glow"></div>
      </div>
    </section>
  );
};

export default About;